import { api } from './client';

export type EnquiryStage =
  | 'new'
  | 'reviewed'
  | 'do-created'
  | 'credit-pending'
  | 'credit-approved'
  | 'credit-rejected'
  | 'pick-pending'
  | 'pick-done'
  | 'packing-done'
  | 'grn-out'
  | 'invoiced'
  | 'gate-out'
  | 'fulfilled'
  | 'cancelled';

export type Priority = 'Low' | 'Normal' | 'High' | 'Urgent';
export type EnquiryFlowType = 'enquiry' | 'order';

export interface EnquiryItem {
  id: number;
  itemId: number | null;
  itemCode: string | null;
  itemName: string;
  hsn: string | null;
  gstPct: string;
  qty: string;
  unit: string;
  rate: string;
  estValue: string;
  discountPct: string | null;
  discountAmt: string | null;
  approvedQty: string | null;
  stockStatus: 'GREEN' | 'AMBER' | 'RED' | null;
  shortfall: string;
  pendingQty: string;
  dispatchedQty: string;
  /** Live available stock (current − reserved) for this line, from the server. */
  availableStock?: number;
  autoMrnRef: string | null;
  item?: { barcode: string | null; imageUrl?: string | null } | null;
}

export interface Enquiry {
  id: number;
  enqNo: string;
  enqDate: string;
  partyId: number;
  party: { id: number; name: string; customerType: string | null };
  deliveryAddress: string | null;
  customerPoRef: string | null;
  expectedDelivery: string | null;
  priority: Priority;
  salesRep: string;
  source: string | null;
  remarks: string | null;
  gstOnRatePc: boolean;
  specialDiscount: string;
  specialDiscountType: 'amount' | 'pct' | null;
  specialDiscountLabel: string | null;
  estOrderValue: string;
  stage: EnquiryStage;
  stageDate: string;
  hasPendingQty: boolean;
  hasMrn: boolean;
  balancePending: boolean;
  flowType: EnquiryFlowType;
  creditStatus: 'GREEN' | 'AMBER' | 'RED' | null;
  creditNote: string | null;
  deliveryStatus: 'In Transit' | 'Delivered' | 'Returned' | null;
  items: EnquiryItem[];
  mrns?: { mrn: { mrnNo: string; status: string } }[];
}

export interface EnquiryListResult {
  data: Enquiry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface EnquiryFilter {
  stage?: string;
  notStage?: string;
  reviewQueue?: boolean;
  flowType?: EnquiryFlowType;
  dateSort?: 'desc' | 'asc';
  /** Exactly these orders, in this order (comma-separated ids). */
  ids?: string;
  priority?: string;
  salesRep?: string;
  partyId?: number;
  /** Filter by the linked party's customerType — 'Lead' or 'Customer'. */
  partyType?: 'Lead' | 'Customer';
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateEnquiryPayload {
  enqDate: string;
  partyId: number;
  deliveryAddress?: string;
  customerPoRef?: string;
  expectedDelivery?: string;
  priority: Priority;
  salesRep: string;
  source?: string;
  remarks?: string;
  gstOnRatePc?: boolean;
  specialDiscount?: number;
  specialDiscountType?: 'amount' | 'pct';
  specialDiscountLabel?: string;
  flowType?: EnquiryFlowType;
  items: {
    itemId?: number;
    itemCode?: string;
    itemName: string;
    hsn?: string;
    gstPct?: number;
    qty: number;
    unit: string;
    rate?: number;
    discountPct?: number;
    discountAmt?: number;
  }[];
}

export async function listEnquiries(filter: EnquiryFilter = {}): Promise<EnquiryListResult> {
  const r = await api.get<EnquiryListResult>('/enquiries', { params: filter });
  return r.data;
}

export async function getEnquiry(id: number): Promise<Enquiry> {
  const r = await api.get<Enquiry>(`/enquiries/${id}`);
  return r.data;
}

export interface MarginResult {
  enqId: number;
  revenue: number;
  cogs: number;
  gp: number;
  gpPct: number;
  lines: {
    enqItemId: number;
    itemName: string;
    qty: number;
    rate: number;
    revenue: number;
    cogs: number;
    gp: number;
    gpPct: number;
  }[];
}

export async function getEnquiryMargin(id: number): Promise<MarginResult> {
  const r = await api.get<MarginResult>(`/enquiries/${id}/margin`);
  return r.data;
}

export async function createEnquiry(payload: CreateEnquiryPayload): Promise<Enquiry> {
  const r = await api.post<Enquiry>('/enquiries', payload);
  return r.data;
}

export async function updateEnquiry(id: number, payload: Partial<CreateEnquiryPayload>): Promise<Enquiry> {
  const r = await api.patch<Enquiry>(`/enquiries/${id}`, payload);
  return r.data;
}

export async function generateOrder(id: number): Promise<Enquiry> {
  const r = await api.post<Enquiry>(`/enquiries/${id}/generate-order`);
  return r.data;
}

export async function cancelEnquiry(id: number, reason: string): Promise<Enquiry> {
  const r = await api.post<Enquiry>(`/enquiries/${id}/cancel`, { reason });
  return r.data;
}

// ── Enquiry review actions ─────────────────────────────────────────────

export async function approveEnquiryFull(id: number): Promise<Enquiry> {
  const r = await api.post<Enquiry>(`/enquiry-review/${id}/approve-full`);
  return r.data;
}

export async function approveEnquiryPartial(
  id: number,
  items: { enqItemId: number; approvedQty: number }[],
): Promise<Enquiry> {
  const r = await api.post<Enquiry>(`/enquiry-review/${id}/approve-partial`, { items });
  return r.data;
}

export async function rejectEnquiry(id: number, reason: string): Promise<Enquiry> {
  const r = await api.post<Enquiry>(`/enquiry-review/${id}/reject`, { reason });
  return r.data;
}

export async function raiseShortageMrn(id: number): Promise<{ mrnIds: number[]; count: number }> {
  const r = await api.post<{ mrnIds: number[]; count: number }>(`/enquiry-review/${id}/raise-mrn`);
  return r.data;
}

/** Unwind a dispatched order (DO + pick/pack + reservations) back to an editable 'new' order. */
export async function reopenOrder(id: number, reason: string): Promise<Enquiry> {
  const r = await api.post<Enquiry>(`/enquiry-review/${id}/reopen`, { reason });
  return r.data;
}

export function enquiriesExportPath(filter: EnquiryFilter = {}): string {
  const search = new URLSearchParams(
    Object.fromEntries(
      Object.entries(filter).filter(([, v]) => v != null && v !== ''),
    ) as Record<string, string>,
  ).toString();
  return `/enquiries/export.xlsx${search ? `?${search}` : ''}`;
}

/** Line-item export path — one row per order line with live warehouse stock + status. */
export function enquiryLinesExportPath(filter: EnquiryFilter = {}): string {
  const search = new URLSearchParams(
    Object.fromEntries(
      Object.entries(filter).filter(([, v]) => v != null && v !== ''),
    ) as Record<string, string>,
  ).toString();
  return `/enquiries/export-lines.xlsx${search ? `?${search}` : ''}`;
}
